"use client";

import React, { useEffect, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";
import Footer from "components/Footers/Footer";
import Navbar from "components/Navbars/AuthNavbar";

export default function ExpenseTreeChartDetailed() {
  const [isDark, setIsDark] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState([]);
  const [currency, setCurrency] = useState("");
  const chartRef = useRef(null);

  const history = useHistory();

  // Redirect if user not logged in
  useEffect(() => {
    if (!localStorage.getItem("userData")) {
      history.push("/login");
    }
  }, [history]);

  // Get groupId
  const storedUserData = localStorage.getItem("userData");
  let groupId = "";
  if (storedUserData) {
    groupId = JSON.parse(storedUserData).groupId;
  }

  // Detect dark mode (Tailwind)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    setIsDark(document.documentElement.classList.contains("dark"));
    return () => observer.disconnect();
  }, []);

  // Fetch Years + Currency
  useEffect(() => {
    const fetchYears = async () => {
      if (!groupId) return;
      const fetchedYears = await apiService.getAvailableYears(groupId);
      setYears(fetchedYears);
    };

    const fetchCurrency = async () => {
      if (!groupId) return;

      const fetchedCurrency = await apiService.getCurrency(groupId);

      if (fetchedCurrency.currency === "INR") {
        setCurrency("₹");
      } else if (fetchedCurrency.currency === "USD") {
        setCurrency("$");
      }
    };

    fetchYears();
    fetchCurrency();
  }, [groupId]);

  // Fetch Tree Data
  useEffect(() => {
    async function fetchData() {
      if (!groupId || !currency || years.length === 0) return;

      try {
        const data = await apiService.getTreeGraphData(groupId, years, currency);
        const nested = buildTree(data, isDark);
        setTreeData(nested);
      } catch (error) {
        console.error("Tree Data Load Failed:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [groupId, currency, years, isDark]);

  // Build Tree UI structure
  function buildTree(flat, isDarkTheme) {
    const idMap = {};
    const roots = [];

    flat.forEach((node) => {
      const level = node.id.split("-").length - 1;

      let symbol = "circle";
      let customColor = "#9ca3af";

      if (node.id === "root") {
        symbol = "diamond";
        customColor = isDarkTheme ? "#fb8b24" : "#e36414";
      } else if (node.parent === "root") {
        symbol = "rect";
        customColor = isDarkTheme ? "#48cae4" : "#0077b6";
      } else if (level === 1) {
        symbol = "circle";
        customColor = isDarkTheme ? "#a7c957" : "#6a994e";
      } else {
        symbol = "diamond";
        customColor = isDarkTheme ? "#89c2d9" : "#02c39a";
      }

      idMap[node.id] = {
        ...node,
        children: [],
        symbol,
        symbolSize: 14,
        itemStyle: { color: customColor },
      };
    });

    flat.forEach((node) => {
      if (node.parent && idMap[node.parent]) {
        idMap[node.parent].children.push(idMap[node.id]);
      } else {
        roots.push(idMap[node.id]);
      }
    });

    return roots;
  }

  const option = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",

    tooltip: {
      trigger: "item",
      backgroundColor: isDark ? "#374151" : "#f9fafb",
      borderColor: isDark ? "#4b5563" : "#e5e7eb",
      textStyle: { color: isDark ? "#f9fafb" : "#111827" },
      formatter: (p) => `<b>${p.data.name}</b>`,
    },

    series: [
      {
        type: "tree",
        data: treeData,
        layout: "orthogonal",
        orient: "LR",

        top: "5%",
        left: "15%",
        bottom: "5%",
        right: "20%",

        symbolSize: 14,

        label: {
          position: "left",
          align: "right",
          color: isDark ? "#f9fafb" : "#111827",
          fontSize: 13,
        },

        leaves: {
          label: {
            position: "right",
            align: "left",
            color: isDark ? "#f9fafb" : "#111827",
            fontSize: 13,
          },
        },

        lineStyle: { color: isDark ? "#6b7280" : "#d1d5db" },

        expandAndCollapse: true,
        initialTreeDepth: 999,

        animationDuration: 350,
        animationDurationUpdate: 350,
        roam: false,

        emphasis: { focus: "descendant" },
        nodePadding: 25,
      },
    ],
  };

  const onChartReady = (chart) => {
    chartRef.current = chart;
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-pulse bg-gray-300 dark:bg-gray-700 rounded-md w-[90%] h-[80%]" />
      </div>
    );
  }

  return (
   <>
     <Navbar />
    <div className="w-full" style={{height: "1700px"}}>
        <h2 className="text-xl font-semibold text-gray-700 dark:text-white" style={{padding: "20px 50px"}}>
          Detailed Category View
        </h2>
      <ReactECharts
        option={option}
        style={{ height: "100%", width: "100%" }}
        onChartReady={onChartReady}
      />
    </div>
    <Footer />
   </>
  );
}
